import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  VortexOrganizationSwitcher,
  type VortexOrgSwitcherOrganization,
  VortexUserButton,
  type VortexUserButtonUser,
} from "@vortexnyc/vortex-core/auth/react";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useMemo } from "react";
import type { z } from "zod";

import { organizationsListMyOrgs, organizationsSetActive } from "~/api-refs";
import {
  orgSwitcherClassNames,
  userButtonClassNames,
} from "~/integrations/vortex-auth/class-names";
import {
  useActiveOrganization,
  useAuthActions,
  useCurrentUser,
} from "~/integrations/vortex-auth/hooks";
import { logger } from "~/lib/logger";
import { buildNavSections } from "~/lib/utils/navigation";

import type { permissionsSchema } from "../lib/validators/merchant-permissions";
import { NavMain, useActiveRoute } from "./nav-main";
import { NotificationCenter } from "./notification-center";
import { ThemeToggle } from "./theme-toggle";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
} from "./ui/sidebar";

type PermissionSet = z.infer<typeof permissionsSchema>;

interface AppSidebarProps {
  permissions?: PermissionSet;
}

export function AppSidebar({ permissions }: AppSidebarProps) {
  const currentPath = useActiveRoute();
  const navigate = useNavigate();
  const location = useRouterState();

  const { user } = useCurrentUser();
  const { organization } = useActiveOrganization();
  const { signOut } = useAuthActions();

  const orgs = useQuery(organizationsListMyOrgs, {});
  const setActive = useMutation(organizationsSetActive);

  const navSections = useMemo(
    () => buildNavSections({ currentPath, permissions }),
    [currentPath, permissions]
  );

  const switcherUser = useMemo<VortexUserButtonUser | null>(() => {
    if (!user) {
      return null;
    }
    return {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? "",
      name: user.fullName ?? user.username ?? null,
    };
  }, [user]);

  const organizations = useMemo<VortexOrgSwitcherOrganization[]>(() => {
    if (!orgs) {
      return [];
    }
    return orgs.map((org) => ({
      _id: org.vortexAuthOrganizationId ?? org.organizationId,
      name: org.businessName || "Organization",
    }));
  }, [orgs]);

  const handleOrgSwitch = useCallback(
    async (vortexOrgId: string) => {
      if (!vortexOrgId || vortexOrgId === organization?.id) {
        return;
      }
      try {
        await setActive({ vortexOrgId });

        let targetPath = location.location.pathname;
        if (targetPath.startsWith("/")) {
          targetPath = targetPath.slice(1);
        }
        const navigationPath = targetPath.length > 0 ? `/${targetPath}` : "/";
        await navigate({ to: navigationPath });
      } catch (error) {
        logger.exception("organization_switch_failed", error);
      }
    },
    [organization?.id, setActive, navigate, location]
  );

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      await navigate({ to: "/sign-in" });
    } catch (error) {
      logger.exception("sign_out_failed", error);
    }
  }, [signOut, navigate]);

  const handleCreateOrganization = useCallback(() => {
    void navigate({ to: "/create-organization" });
  }, [navigate]);

  const handleManageAccount = useCallback(() => {
    void navigate({ to: "/settings/account/$", params: { _splat: undefined } });
  }, [navigate]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <VortexOrganizationSwitcher
          classNames={orgSwitcherClassNames}
          currentOrganizationId={organization?.id ?? null}
          onCreateOrganization={handleCreateOrganization}
          onSelectOrganization={handleOrgSwitch}
          organizations={organizations}
        />
      </SidebarHeader>

      <SidebarContent>
        <NavMain sections={navSections} />
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem className="list-none">
            <NotificationCenter />
          </SidebarMenuItem>
          <SidebarMenuItem className="list-none">
            <ThemeToggle />
          </SidebarMenuItem>
          <SidebarMenuItem className="list-none">
            <VortexUserButton
              classNames={userButtonClassNames}
              onManageAccount={handleManageAccount}
              onSignOut={handleSignOut}
              user={switcherUser}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
